import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpenText,
  Box,
  GraduationCap,
  ChevronDown,
  Download,
  FileText,
  FolderKanban,
  Grid2x2,
  Lightbulb,
  Mail,
  MailCheck,
  MapPinned,
  PenSquare,
  PhoneCall,
  BriefcaseBusiness,
  Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardSidebar from "./components/DashboardSidebar";
import ContentManager from "./components/ContentManager";
import UsersManager from "./components/UsersManager";
import BlogManager from "./components/BlogManager";
import BoardsManager from "./components/BoardsManager";
import AccessoriesManager from "./components/MetalsManager";
import MetalFramesManager from "./components/MetalFramesManager";
import ProjectsManager from "./components/ProjectsManager";
import SolutionsManager from "./components/SolutionsManager";
import SectorsManager from "./components/SectorsManager";
import DownloadsManager from "./components/DownloadsManager";
import CoursesManager from "./components/CoursesManager";
import TrainingsManager from "./components/TrainingsManager";
import CallsRequestedManager from "./components/CallsRequestedManager";
import ContactUsManager from "./components/ContactUsManager";
import SubmittedQuestionsManager from "./components/SubmittedQuestionsManager";
import CareersManager from "./components/CareersManager";
import SubscribersManager from "./components/SubscribersManager";

const CONTENT_PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "About Us" },
  { key: "products", label: "Products" },
  { key: "allsolutions", label: "Solutions" },
  { key: "careers", label: "Careers" },
  { key: "services", label: "Services" },
  { key: "sectors", label: "Sectors" },
  { key: "projects", label: "All Projects" },
  { key: "estimator", label: "Estimator" },
  { key: "blog", label: "Blog" },
  { key: "downloads", label: "Downloads" },
  { key: "academy", label: "Academy" },
  { key: "contactus", label: "Contact" },
  { key: "terms", label: "Terms" },
  { key: "shared", label: "Footer" },
];

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState("content");
  const [selectedPage, setSelectedPage] = useState("home");
  const [selectedProductType, setSelectedProductType] = useState("boards");

  const navItems = [
    { id: "overview", label: t("dashboard.overview"), icon: Grid2x2 },
    { id: "content", label: t("dashboard.content"), icon: FileText },
    { id: "products", label: "Products", icon: Box },
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "solutions", label: "Solutions", icon: Lightbulb },
    { id: "sectors", label: "Sectors", icon: MapPinned },
    { id: "careers", label: "Careers", icon: BriefcaseBusiness },
    { id: "courses", label: "Courses", icon: BookOpenText },
    { id: "trainings", label: "Trainings", icon: GraduationCap },
    { id: "downloads", label: "Downloads", icon: Download },
    { id: "calls-requested", label: "Calls Requested", icon: PhoneCall },
    { id: "contact-us", label: "Contact Us", icon: Mail },
    { id: "subscribers", label: "Subscribed", icon: MailCheck },
    { id: "users", label: t("dashboard.users"), icon: Users },
    { id: "blog", label: "Blog", icon: PenSquare },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-slate-100 lg:h-screen lg:overflow-hidden">
      <div className="flex min-h-screen lg:h-full">
        <DashboardSidebar
          navItems={navItems}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onLogout={handleLogout}
          t={t}
        />

        <main className="flex-1 min-w-0 lg:h-full lg:overflow-y-auto p-3 pt-20 sm:p-4 sm:pt-20 lg:p-8 lg:pt-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4 flex flex-wrap items-start sm:items-center justify-between gap-3">
              {activeSection === "content" ? (
                <div className="min-w-[220px]">
                  <p className="text-sm text-slate-600 mb-2">Please select which page you want to edit</p>
                  <div className="relative">
                    <select
                      value={selectedPage}
                      onChange={(e) => setSelectedPage(e.target.value)}
                      className="appearance-none w-full sm:min-w-[260px] pr-9 pl-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Select content page"
                    >
                      {CONTENT_PAGES.map((page) => (
                        <option key={page.key} value={page.key}>
                          {page.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={16}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                  </div>
                </div>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-2 sm:gap-3">
                {["en", "ar", "ku"].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(lang);
                      localStorage.setItem("irqgyp_lang", lang);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${
                      i18n.language === lang
                        ? "bg-slate-900 text-white border-slate-900"
                        : "border-slate-300 hover:bg-white"
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {activeSection === "content" ? (
              <ContentManager t={t} selectedPage={selectedPage} />
            ) : activeSection === "products" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Products</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Products Editor</h1>
                <p className="text-slate-600 mb-4">Select product category for the next phase.</p>
                <div className="max-w-[320px] relative">
                  <select
                    value={selectedProductType}
                    onChange={(e) => setSelectedProductType(e.target.value)}
                    className="appearance-none w-full pr-9 pl-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Select products category"
                  >
                    <option value="boards">Boards</option>
                    <option value="accessories">Accessories</option>
                    <option value="drywall-metal-frame">Drywall metal frame</option>
                    <option value="ceiling-metal-frame">Ceiling metal frame</option>
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                </div>
                {selectedProductType === "boards" ? (
                  <div className="mt-6">
                    <BoardsManager />
                  </div>
                ) : selectedProductType === "accessories" ? (
                  <div className="mt-6">
                    <AccessoriesManager />
                  </div>
                ) : selectedProductType === "drywall-metal-frame" ? (
                  <div className="mt-6">
                    <MetalFramesManager
                      frameType="drywall-metal-frame"
                      title="Drywall Metal Frames"
                    />
                  </div>
                ) : (
                  <div className="mt-6">
                    <MetalFramesManager
                      frameType="ceiling-metal-frame"
                      title="Ceiling Metal Frames"
                    />
                  </div>
                )}
              </div>
            ) : activeSection === "solutions" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Solutions</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Solutions Editor</h1>
                <p className="text-slate-600 mb-4">Create and update solution entries used by solution detail pages.</p>
                <SolutionsManager />
              </div>
            ) : activeSection === "sectors" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Sectors</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Sectors Editor</h1>
                <p className="text-slate-600 mb-4">Manage sectors content, images, benefits, project block and gallery.</p>
                <SectorsManager />
              </div>
            ) : activeSection === "careers" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Careers</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Careers Manager</h1>
                <p className="text-slate-600 mb-4">Manage jobs and review submitted applications.</p>
                <CareersManager />
              </div>
            ) : activeSection === "courses" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Courses</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Courses Editor</h1>
                <p className="text-slate-600 mb-4">Manage courses, descriptions and gain items for academy pages.</p>
                <CoursesManager />
              </div>
            ) : activeSection === "trainings" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Trainings</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Trainings Editor</h1>
                <p className="text-slate-600 mb-4">Manage course trainings, benefits and trainer avatars per course.</p>
                <TrainingsManager />
              </div>
            ) : activeSection === "downloads" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Downloads</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Downloads Editor</h1>
                <p className="text-slate-600 mb-4">Manage downloadable PDF files, titles, language and active status.</p>
                <DownloadsManager />
              </div>
            ) : activeSection === "calls-requested" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Calls Requested</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Calls Requested Editor</h1>
                <p className="text-slate-600 mb-4">Manage call request services and review incoming call requests.</p>
                <CallsRequestedManager />
              </div>
            ) : activeSection === "contact-us" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Contact Us</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Contact Us Requests</h1>
                <p className="text-slate-600 mb-4">See submitted contact requests and delete them when needed.</p>
                <div className="space-y-12">
                  <ContactUsManager />
                  <div className="border-t border-slate-200 pt-10">
                    <SubmittedQuestionsManager />
                  </div>
                </div>
              </div>
            ) : activeSection === "subscribers" ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Subscribed</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Newsletter Subscribers</h1>
                <p className="text-slate-600 mb-4">See who has subscribed from the footer newsletter form and remove entries when needed.</p>
                <SubscribersManager />
              </div>
            ) : activeSection === "users" ? (
              <UsersManager />
            ) : activeSection === "blog" ? (
              <BlogManager />
            ) : activeSection === "projects" ? (
              <ProjectsManager />
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 lg:p-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Dashboard</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{t("dashboard.loggedIn")}</h1>
                <p className="text-slate-600">This section will be added in next phase.</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
